import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import RecordPage from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Books, Users, ChartLineUp, Plus, Exam } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { stripHtml } from '../../lib/htmlText';

export default function InstructorDashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, coursesRes] = await Promise.all([
        api.get('/reports/dashboard'),
        api.get('/courses')
      ]);
      setStats(statsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="instructor-dashboard"
        title="Dashboard"
        subtitle={`Welcome back, ${user?.first_name || ''}!`}
        onBack={null}
        findable={false}
        onRefresh={fetchData}
        refreshing={loading}
        loading={loading}
        maxWidth="none"
        primary={[
          { key: 'new-course', label: 'New course', icon: Plus, onClick: () => navigate('/instructor/courses/new'), testid: 'create-course-btn' },
        ]}
        secondary={[
          { key: 'my-courses', label: 'My courses', icon: Books, onClick: () => navigate('/instructor/courses') },
          { key: 'exams', label: 'Exams', icon: Exam, onClick: () => navigate('/instructor/exams') },
        ]}
      >
        {/* KPIs */}
        <StatTiles items={[
          { label: 'My courses', value: stats.my_courses || 0, icon: Books },
          { label: 'My students', value: stats.my_students || 0, icon: Users, iconClass: 'bg-amber-100 text-amber-700' },
          { label: 'Total enrollments', value: stats.total_enrollments || 0, icon: ChartLineUp, iconClass: 'bg-sky-100 text-sky-700' },
        ]} />

        {/* My Courses */}
        <DataGrid
          title="My courses"
          rows={courses}
          loading={loading}
          onRefresh={fetchData}
          selectable={false}
          pagination={false}
          dense
          searchKeys={['title', 'course_code']}
          columns={[
            {
              key: 'title', header: 'Title', accessor: (c) => c.title || '',
              render: (c) => (
                <div className="min-w-0" data-testid={`course-${c.id}`}>
                  <div className="font-medium truncate">{c.title}</div>
                  {c.description && (
                    <div className="text-xs text-muted-foreground truncate">{stripHtml(c.description, 100)}</div>
                  )}
                </div>
              ),
            },
            { key: 'course_code', header: 'Code', width: 140, accessor: (c) => c.course_code || '', className: 'font-mono text-xs' },
            { key: 'enrollment_count', header: 'Enrollments', width: 120, align: 'right', accessor: (c) => c.enrollment_count || 0, className: 'tabular-nums' },
            { key: 'is_published', header: 'Published', width: 120, accessor: (c) => !!c.is_published, render: (c) => <StatusBadge value={c.is_published ? 'published' : 'draft'} /> },
          ]}
          rowTone={(c) => (c.is_published ? 'success' : null)}
          onRowDoubleClick={(c) => navigate(`/instructor/courses/${c.id}`)}
          emptyMessage="You haven't created any courses yet. Use “New course” to create your first one."
        />
      </RecordPage>
    </DashboardLayout>
  );
}
